import axios from "axios";
import { createContext, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./uselocalstorage";
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage("user", null);
  const [currentConfig, setCurrentConfig] = useLocalStorage("currentConfig", null);
  const navigate = useNavigate();

  const changeCurrentConfig = (config) => {
    setCurrentConfig(config);
  };

  const getCurrentConfig = () => {
    
    return JSON.parse(window.localStorage.getItem("currentConfig"));
  };

  // call this function when you want to authenticate the user
  const login = async (data) => {
    //call your api to authenticate the user
    const url = process.env.REACT_APP_BACKEND_API + "/auth/signin";
    let result = await axios
      .post(url, data)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
    if (result.data.accessToken) {
      setUser(result.data);
    }
  };

  // call this function to sign out logged in user
  const logout = () => {
    setUser(null);
    setCurrentConfig(null);
    navigate("/", { replace: true });
  };

  const impersonateUser = async (targetUserId) => {
    try {
      const adminToken = user?.accessToken || window.localStorage.getItem("accessToken");
      if (!adminToken) {
        throw new Error("No active admin token found");
      }

      const url = process.env.REACT_APP_BACKEND_API + "/auth/impersonate";
      const response = await axios.post(
        url,
        { userId: targetUserId },
        {
          headers: {
            "x-access-token": adminToken,
          },
        }
      );

      const { accessToken, ...userData } = response.data;

      // Keep original admin session data
      window.localStorage.setItem("adminAccessToken", adminToken);
      window.localStorage.setItem("adminUser", JSON.stringify(user));

      // Overwrite storage keys
      window.localStorage.setItem("accessToken", accessToken);
      window.localStorage.setItem("currentUser", JSON.stringify(userData));

      // Update the main "user" key that the app uses
      const impersonatedUser = {
        accessToken,
        ...userData,
      };
      setUser(impersonatedUser);

      // Force a full reload to clear state and reload routes/views as the target user
      window.location.href = "/";
    } catch (error) {
      console.error(
        "Impersonation failed:",
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const stopImpersonating = () => {
    const originalAdminUser = window.localStorage.getItem("adminUser");
    const originalAdminToken = window.localStorage.getItem("adminAccessToken");

    if (originalAdminUser && originalAdminToken) {
      const parsedAdminUser = JSON.parse(originalAdminUser);
      // Restore the admin session
      setUser(parsedAdminUser);
      window.localStorage.setItem("accessToken", originalAdminToken);

      // Clean up the backup storage keys
      window.localStorage.removeItem("adminAccessToken");
      window.localStorage.removeItem("adminUser");
      window.localStorage.removeItem("currentUser");

      // Redirect to the admin dashboard / ejecutivos
      window.location.href = "/admin/dashboard";
    } else {
      // Fallback
      setUser(null);
      setCurrentConfig(null);
      window.localStorage.clear();
      window.location.href = "/";
    }
  };

  const isImpersonating = () => {
    return window.localStorage.getItem("adminAccessToken") !== null;
  };

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      currentConfig,
      changeCurrentConfig,
      getCurrentConfig,
      impersonateUser,
      stopImpersonating,
      isImpersonating,
    }),
    [user, currentConfig]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
